import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { User } from '@foodstyles/shared';
import { apiGet } from '../lib/api';
import { qk } from '../lib/queryKeys';
import type { MeResponse } from '../types/api';

export type AuthStatus = 'loading' | 'unauthenticated' | 'pending' | 'active';

interface AuthValue {
  status: AuthStatus;
  user?: User;
  permissions?: { manage: boolean; manageUsers: boolean; approveTimeOff: boolean };
  refetch: () => void;
}

const AuthContext = createContext<AuthValue>({ status: 'loading', refetch: () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: qk.me,
    queryFn: () => apiGet<MeResponse>('/api/me'),
    staleTime: Infinity,
    retry: false,
  });

  let status: AuthStatus = 'loading';
  if (!q.isLoading) {
    if (!q.data?.authenticated || !q.data.user) status = 'unauthenticated';
    else if (q.data.user.status === 'Pending') status = 'pending';
    else if (q.data.user.status === 'Active') status = 'active';
    else status = 'unauthenticated';
  }

  return (
    <AuthContext.Provider
      value={{ status, user: q.data?.user, permissions: q.data?.permissions, refetch: () => void q.refetch() }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthValue => useContext(AuthContext);
